import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Modal, Form, Select, InputNumber, Input, RadioGroup, Row, Col, Alert, Tag, Button, message,
} from '../../ui';
import { request } from '../../api/client';
import { FileUploadField } from '../../components/FileUploadField';
import { formatIstDate } from '../../lib/time';

const REASON_OPTIONS = [
  { value: 'PHYSICAL_COUNT', label: 'Physical count correction' },
  { value: 'DAMAGE', label: 'Damaged stock' },
  { value: 'EXPIRY', label: 'Expired stock write-off' },
  { value: 'THEFT', label: 'Shrinkage / theft' },
  { value: 'SAMPLE', label: 'Free sample / marketing' },
  { value: 'PRODUCTION_LOSS', label: 'Production loss' },
  { value: 'OTHER', label: 'Other (specify in notes)' },
];

type Props = {
  open: boolean;
  onClose: () => void;
  editing?: any; // when set, the modal edits this (SUBMITTED-only) adjustment instead of creating one
};

export function AdjustmentFormModal({ open, onClose, editing }: Props) {
  const qc = useQueryClient();
  const [form] = Form.useForm();

  const { data: warehouses } = useQuery({
    queryKey: ['warehouses'],
    queryFn: () => request<any[]>({ url: '/warehouses' }),
    enabled: open,
  });

  const productId = Form.useWatch('productId', form);
  const warehouseId = Form.useWatch('warehouseId', form);
  const batchId = Form.useWatch('batchId', form);
  const direction = Form.useWatch('direction', form);

  // Catalog with live per-warehouse stock — the adjustment product list is what's actually in the
  // chosen warehouse (with the available qty), so you adjust against real stock. To add brand-new
  // stock that isn't tracked here yet, use Add Stock / GRN instead.
  const { data: catalog } = useQuery({
    queryKey: ['catalog-with-stock-adj'],
    queryFn: () => request<any[]>({ url: '/catalog/with-stock' }),
    enabled: open,
  });

  const { data: batches } = useQuery({
    queryKey: ['batches-for-adj', productId, warehouseId],
    queryFn: () => request<any[]>({ url: '/batches', params: { productId, warehouseId } }),
    enabled: !!productId && !!warehouseId,
  });

  // For an OUT adjustment against a specific batch, the qty can't exceed what's
  // available in that batch — cap it client-side rather than bouncing off the server.
  const selectedBatch = (batches ?? []).find((b: any) => b.id === batchId);
  const batchAvail = selectedBatch != null ? Number(selectedBatch.availableQty ?? selectedBatch.qty ?? 0) : undefined;
  const outCap = direction === 'OUT' && batchAvail != null ? batchAvail : undefined;

  // Products actually in stock in the chosen warehouse, with the available qty there.
  const whCode = (warehouses ?? []).find((w: any) => String(w.id) === String(warehouseId))?.code as string | undefined;
  const inStock = (catalog ?? [])
    .map((p: any) => ({ productId: String(p.productId), sku: p.sku, name: p.name, variantLabel: p.variantLabel, avail: Number(p.stock?.warehouses?.[whCode ?? ''] ?? 0) }))
    .filter((p) => p.avail > 0);

  // Seeding is done via the Form's `initialValues` + a per-target `key` (see <Form> below), NOT via
  // setFieldsValue in an on-open effect — that raced the Radix modal's remount (blank / stale fields).

  // Clear the picked product + batch when the USER changes the warehouse — but NOT on the initial
  // edit prefill. Baseline the tracker to the seeded warehouse each time the modal opens so the
  // initialValues prefill isn't mistaken for a user change (which would wipe the loaded values).
  const prevWh = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (open) prevWh.current = editing?.warehouseId != null ? String(editing.warehouseId) : undefined;
  }, [open, editing]);
  useEffect(() => {
    if (prevWh.current !== undefined && prevWh.current !== warehouseId) {
      form.setFieldsValue({ productId: undefined, batchId: undefined });
    }
    prevWh.current = warehouseId;
  }, [warehouseId, form]);

  const create = useMutation({
    mutationFn: (vals: any) => editing
      ? request({ url: `/stock-adjustments/${editing.id}`, method: 'PATCH', data: vals })
      : request({ url: '/stock-adjustments', method: 'POST', data: vals }),
    onSuccess: () => {
      message.success(editing ? 'Adjustment updated' : 'Adjustment submitted for approval');
      qc.invalidateQueries({ queryKey: ['stock-adjustments'] });
      qc.invalidateQueries({ queryKey: ['stock-adjustments-all'] });
      onClose();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });

  return (
    <Modal
      open={open}
      onOpenChange={(o) => { if (!o) onClose(); }}
      title={editing ? 'Edit Stock Adjustment' : 'New Stock Adjustment'}
      width={720}
      footer={
        <>
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={create.isPending} onClick={() => form.submit()}>{editing ? 'Save changes' : 'Submit for approval'}</Button>
        </>
      }
    >
      <Alert
        type="warning"
        style={{ marginBottom: 12 }}
        title="Adjustments change ledger stock and need approval before they post."
        description="Attach photo/document evidence for damage, expiry or theft entries — auditors will look for it."
      />
      <Form
        form={form}
        key={editing ? `edit-${editing.id}` : 'create'}
        layout="vertical"
        initialValues={editing ? {
          warehouseId: editing.warehouseId != null ? String(editing.warehouseId) : undefined,
          productId: editing.productId != null ? String(editing.productId) : undefined,
          batchId: editing.batchId != null ? String(editing.batchId) : undefined,
          qty: Number(editing.qty),
          direction: editing.direction,
          reason: editing.reason,
          evidenceUrl: editing.evidenceUrl ?? undefined,
          notes: editing.notes ?? undefined,
        } : { direction: 'OUT', reason: 'PHYSICAL_COUNT' }}
        onFinish={(v) => create.mutate(v)}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="warehouseId" label="Warehouse" rules={[{ required: true }]}>
              <Select
                showSearch
                optionFilterProp="searchLabel"
                placeholder="Where is the stock?"
                options={(warehouses ?? []).map((w: any) => ({
                  value: w.id,
                  searchLabel: `${w.code} · ${w.name}`,
                  label: `${w.code} · ${w.name}`,
                  isMaster: !!w.isMaster,
                }))}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="productId" label="Product" rules={[{ required: true }]}>
              <Select
                showSearch
                optionFilterProp="searchLabel"
                placeholder={warehouseId ? 'Product in stock here' : 'Select a warehouse first'}
                disabled={!warehouseId}
                notFoundContent={warehouseId ? 'No stock in this warehouse' : 'Select a warehouse'}
                options={inStock.map((p) => ({
                  value: p.productId,
                  searchLabel: `${p.sku} · ${p.name}${p.variantLabel ? ` (${p.variantLabel})` : ''}`,
                  label: (
                    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.sku} · {p.name}{p.variantLabel ? ` (${p.variantLabel})` : ''}</span>
                      <Tag tone="blue" style={{ margin: 0, flexShrink: 0 }}>{p.avail}</Tag>
                    </span>
                  ),
                }))}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="batchId"
              label="Batch"
              tooltip="Required when the product is batch-tracked. Pick the actual batch you are adjusting."
            >
              <Select
                allowClear
                showSearch
                optionFilterProp="searchLabel"
                placeholder={productId && warehouseId ? 'Pick a batch (optional)' : 'Select product & warehouse first'}
                disabled={!productId || !warehouseId}
                options={(batches ?? []).map((b: any) => ({
                  value: b.id,
                  searchLabel: b.batchNo,
                  label: (
                    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, minWidth: 0 }}>
                      <span style={{ fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.batchNo}</span>
                      <span style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
                        <Tag tone="blue" style={{ margin: 0 }}>{b.availableQty ?? b.qty ?? 0}</Tag>
                        {b.expDate && <span style={{ fontSize: 11, color: '#8c8c8c', whiteSpace: 'nowrap' }}>exp {formatIstDate(b.expDate)}</span>}
                      </span>
                    </span>
                  ),
                }))}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="qty"
              label="Quantity"
              dependencies={['direction', 'batchId']}
              extra={outCap != null ? `Available in batch: ${outCap}` : undefined}
              rules={[
                { required: true, type: 'number', min: 0.0001 },
                () => ({
                  validator(_: any, v: any) {
                    if (outCap != null && Number(v) > outCap) {
                      return Promise.reject(new Error(`Only ${outCap} available in this batch`));
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <InputNumber min={0.001} max={outCap} style={{ width: '100%' }} step={1} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="direction" label="Direction" rules={[{ required: true }]}>
              <RadioGroup
                inline
                options={[
                  { value: 'IN', label: 'IN +' },
                  { value: 'OUT', label: 'OUT −' },
                ]}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="reason" label="Reason" rules={[{ required: true }]}>
          <Select options={REASON_OPTIONS} />
        </Form.Item>

        <Form.Item name="evidenceUrl" label="Evidence (photo / document)"
          extra="Photo or PDF supporting the adjustment — auditors will look for it on damage / expiry / theft entries.">
          <FileUploadField category="evidence" buttonLabel="Upload evidence" />
        </Form.Item>

        <Form.Item name="notes" label="Notes">
          <Input.TextArea rows={3} placeholder="Free text — what happened, why, who saw it." />
        </Form.Item>
      </Form>
    </Modal>
  );
}
